import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Film, Star, User, Plus, Lightbulb, Check, Trash2, CheckCircle, Circle } from 'lucide-react';
import { api } from '@watchparty/shared';
import { Select } from '@/design-system/Inputs';
import { tmdbImage, workTypeLabel } from '@/lib/utils';
import { useAuth } from '@/features/auth';
import { DiscussionTab } from '@/pages/parties/DiscussionTab';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
// ---------------------------------------------------------------------------
// Status constants
// ---------------------------------------------------------------------------
const STATUS_WATCHED = 1;
const STATUS_WATCHING = 2;
const STATUS_WANTS_TO_WATCH = 3;
const statusOptions = [
    { value: STATUS_WANTS_TO_WATCH, label: 'Want to Watch' },
    { value: STATUS_WATCHING, label: 'Watching' },
    { value: STATUS_WATCHED, label: 'Watched' },
];
// ---------------------------------------------------------------------------
// WatchStatusControl (light background variant)
// ---------------------------------------------------------------------------
function WatchStatusControl({ workId, currentStatus, workType, }) {
    const queryClient = useQueryClient();
    const isSeries = workType === 2;
    const handleClick = useCallback(async (status) => {
        const key = ['work', workId];
        const clearing = status === currentStatus;
        queryClient.setQueryData(key, (old) => {
            if (!old)
                return old;
            return {
                ...old,
                myStatus: clearing ? null : status,
                ...(clearing ? { myRating: null } : {}),
            };
        });
        try {
            if (clearing) {
                await api.delete(`/users/me/works/${workId}/status${isSeries ? '?cascade=true' : ''}`);
            }
            else {
                await api.put(`/users/me/works/${workId}/status`, { status, ...(isSeries ? { cascade: true } : {}) });
            }
            queryClient.invalidateQueries({ queryKey: key });
            if (isSeries)
                queryClient.invalidateQueries({ queryKey: ['work', workId, 'seasons'] });
        }
        catch {
            queryClient.invalidateQueries({ queryKey: key });
            if (isSeries)
                queryClient.invalidateQueries({ queryKey: ['work', workId, 'seasons'] });
        }
    }, [workId, currentStatus, queryClient, isSeries]);
    return (_jsx("div", { style: { display: 'flex', gap: '6px', flexWrap: 'wrap' }, children: statusOptions.map((opt) => {
            const active = currentStatus === opt.value;
            return (_jsx("button", { onClick: () => handleClick(opt.value), style: {
                    padding: '5px 12px',
                    borderRadius: 'var(--radius-full)',
                    border: `1px solid ${active ? 'transparent' : 'var(--color-border)'}`,
                    background: active ? 'var(--color-accent)' : 'transparent',
                    color: active ? 'var(--color-bg)' : 'var(--color-text-muted)',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    fontFamily: 'var(--font-family-body)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 80ms ease',
                }, children: opt.label }, opt.value));
        }) }));
}
// ---------------------------------------------------------------------------
// StarRating (light background variant)
// ---------------------------------------------------------------------------
function StarRating({ workId, currentRating, }) {
    const queryClient = useQueryClient();
    const handleClick = useCallback(async (star) => {
        const key = ['work', workId];
        const clearing = star === currentRating;
        queryClient.setQueryData(key, (old) => {
            if (!old)
                return old;
            return { ...old, myRating: clearing ? null : star };
        });
        try {
            if (clearing) {
                await api.delete(`/users/me/works/${workId}/rating`);
            }
            else {
                await api.put(`/users/me/works/${workId}/rating`, { rating: star });
            }
            queryClient.invalidateQueries({ queryKey: key });
        }
        catch {
            queryClient.invalidateQueries({ queryKey: key });
        }
    }, [workId, currentRating, queryClient]);
    return (_jsx("div", { style: { display: 'flex', gap: '2px' }, children: [1, 2, 3, 4, 5].map((star) => {
            const filled = currentRating != null && star <= currentRating;
            return (_jsx("button", { onClick: () => handleClick(star), style: {
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    transition: 'opacity 80ms ease',
                }, children: _jsx(Star, { size: 20, fill: filled ? 'var(--color-text)' : 'none', stroke: filled ? 'var(--color-text)' : 'var(--color-border-strong)', strokeWidth: 2 }) }, star));
        }) }));
}
// ---------------------------------------------------------------------------
// PersonCard
// ---------------------------------------------------------------------------
function PersonCard({ name, subtitle, profilePath, }) {
    return (_jsxs("div", { style: {
            width: '90px', flexShrink: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: '6px',
        }, children: [_jsx("div", { style: {
                    width: '56px', height: '56px',
                    borderRadius: '50%', overflow: 'hidden',
                    background: 'var(--color-surface-sunken)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }, children: profilePath ? (_jsx("img", { src: tmdbImage(profilePath, 'w185'), alt: name, loading: "lazy", style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } })) : (_jsx(User, { size: 22, style: { color: 'var(--color-text-faint)' } })) }), _jsxs("div", { style: { width: '100%', textAlign: 'center' }, children: [_jsx("p", { style: {
                            margin: 0,
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.75rem', fontWeight: 600,
                            color: 'var(--color-text)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }, children: name }), subtitle && (_jsx("p", { style: {
                            margin: '2px 0 0',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.7rem',
                            color: 'var(--color-text-muted)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }, children: subtitle }))] })] }));
}
// ---------------------------------------------------------------------------
// SeasonsSection
// ---------------------------------------------------------------------------
function SeasonsSection({ workId }) {
    const [selectedSeasonId, setSelectedSeasonId] = useState('');
    const queryClient = useQueryClient();
    const { data: seasons } = useQuery({
        queryKey: ['work', workId, 'seasons'],
        queryFn: () => api.get(`/works/${workId}/seasons`),
    });
    const toggleEpisode = useCallback(async (epId, isWatched) => {
        queryClient.setQueryData(['work', workId, 'seasons'], (old) => {
            if (!old)
                return old;
            return {
                ...old,
                totalWatchedEpisodes: old.totalWatchedEpisodes + (isWatched ? -1 : 1),
                seasons: old.seasons.map((s) => {
                    const epIdx = s.episodes.findIndex((e) => e.workId === epId);
                    if (epIdx === -1)
                        return s;
                    return {
                        ...s,
                        watchedEpisodeCount: s.watchedEpisodeCount + (isWatched ? -1 : 1),
                        episodes: s.episodes.map((e) => e.workId === epId ? { ...e, myStatus: isWatched ? null : STATUS_WATCHED } : e),
                    };
                }),
            };
        });
        try {
            if (isWatched) {
                await api.delete(`/users/me/works/${epId}/status`);
            }
            else {
                await api.put(`/users/me/works/${epId}/status`, { status: STATUS_WATCHED });
            }
        }
        catch { /* ignore */ }
        queryClient.invalidateQueries({ queryKey: ['work', workId, 'seasons'] });
        queryClient.invalidateQueries({ queryKey: ['work', workId] });
    }, [workId, queryClient]);
    const toggleSeason = useCallback(async (seasonId, allWatched) => {
        try {
            if (allWatched) {
                await api.delete(`/users/me/works/${seasonId}/status?cascade=true`);
            }
            else {
                await api.put(`/users/me/works/${seasonId}/status`, { status: STATUS_WATCHED, cascade: true });
            }
        }
        catch { /* ignore */ }
        queryClient.invalidateQueries({ queryKey: ['work', workId, 'seasons'] });
        queryClient.invalidateQueries({ queryKey: ['work', workId] });
    }, [workId, queryClient]);
    if (!seasons || seasons.seasonCount === 0)
        return null;
    const selectedSeason = seasons.seasons.find((s) => s.workId === selectedSeasonId);
    const selectedAllWatched = selectedSeason
        ? selectedSeason.episodeCount > 0 && selectedSeason.watchedEpisodeCount === selectedSeason.episodeCount
        : false;
    return (_jsxs("div", { style: { marginBottom: '24px' }, children: [_jsxs("h3", { style: {
                    fontFamily: 'var(--font-family-body)',
                    fontSize: '0.75rem', fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'var(--color-text-muted)',
                    marginBottom: '12px',
                }, children: ["Seasons (", seasons.seasonCount, ")"] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsxs(Select, { value: selectedSeasonId, onChange: (e) => setSelectedSeasonId(e.target.value), style: { flex: 1 }, children: [_jsx("option", { value: "", children: "Select Season" }), seasons.seasons.map((season) => (_jsxs("option", { value: season.workId, children: [season.title, " (", season.watchedEpisodeCount, "/", season.episodeCount, ")"] }, season.workId)))] }), selectedSeason && (_jsx("button", { onClick: () => toggleSeason(selectedSeason.workId, selectedAllWatched), style: {
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: '4px', display: 'flex', flexShrink: 0,
                        }, title: selectedAllWatched ? 'Clear season' : 'Mark season watched', children: selectedAllWatched
                            ? _jsx(CheckCircle, { size: 20, style: { color: 'var(--color-accent)' } })
                            : _jsx(Circle, { size: 20, style: { color: 'var(--color-text-muted)' } }) }))] }), selectedSeason && selectedSeason.episodes.length > 0 && (_jsx("div", { style: { marginTop: '12px', display: 'flex', flexDirection: 'column' }, children: selectedSeason.episodes.map((ep) => {
                    const epWatched = ep.myStatus === STATUS_WATCHED;
                    return (_jsxs("div", { style: {
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '8px 0',
                        }, children: [_jsx("button", { onClick: () => toggleEpisode(ep.workId, epWatched), style: {
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    padding: '2px', display: 'flex', flexShrink: 0,
                                }, children: epWatched
                                    ? _jsx(CheckCircle, { size: 18, style: { color: 'var(--color-accent)' } })
                                    : _jsx(Circle, { size: 18, style: { color: 'var(--color-text-muted)' } }) }), _jsxs("span", { style: {
                                    fontSize: '0.7rem', fontWeight: 700,
                                    color: 'var(--color-text-muted)',
                                    background: 'var(--color-surface-sunken)',
                                    padding: '2px 6px',
                                    borderRadius: 'var(--radius-sm)',
                                    flexShrink: 0,
                                    fontFamily: 'var(--font-family-body)',
                                }, children: ["E", ep.episodeNumber] }), ep.stillUrl && (_jsx("div", { style: {
                                    width: '64px', height: '36px', flexShrink: 0,
                                    borderRadius: 'var(--radius-sm)', overflow: 'hidden',
                                    background: 'var(--color-surface-sunken)',
                                }, children: _jsx("img", { src: tmdbImage(ep.stillUrl, 'w185'), alt: ep.title, loading: "lazy", style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } }) })), _jsxs("div", { style: { minWidth: 0, flex: 1 }, children: [_jsx("p", { style: {
                                            margin: 0,
                                            fontFamily: 'var(--font-family-body)',
                                            fontSize: '0.85rem', fontWeight: 500,
                                            color: 'var(--color-text)',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }, children: ep.title }), ep.runtimeMinutes && (_jsxs("p", { style: {
                                            margin: '1px 0 0',
                                            fontFamily: 'var(--font-family-body)',
                                            fontSize: '0.75rem',
                                            color: 'var(--color-text-faint)',
                                        }, children: [ep.runtimeMinutes, "m"] }))] })] }, ep.workId));
                }) }))] }));
}
export function WorkDetailPage() {
    const { workId } = useParams({ strict: false });
    const { partyId: searchPartyId } = useSearch({ strict: false });
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const partyId = searchPartyId ?? user?.defaultPartyId ?? null;
    const [activeTab, setActiveTab] = useState('details');
    const [isAdding, setIsAdding] = useState(false);
    const [suggested, setSuggested] = useState(false);
    const [addError, setAddError] = useState(null);
    const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
    const [removeTies, setRemoveTies] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const { data: work, isLoading } = useQuery({
        queryKey: ['work', workId],
        queryFn: () => api.get(`/works/${workId}`),
    });
    const { data: partyWorkIds } = useQuery({
        queryKey: ['party', partyId, 'work-ids'],
        queryFn: () => api.get(`/parties/${partyId}/work-ids`),
        enabled: !!partyId,
    });
    const { data: partyDetail } = useQuery({
        queryKey: ['party', partyId],
        queryFn: () => api.get(`/parties/${partyId}`),
        enabled: !!partyId,
    });
    const isInParty = partyWorkIds?.workIds?.includes(workId) ?? false;
    const isOwner = partyDetail?.members?.some((m) => m.isOwner && m.userId === user?.userId) ?? false;
    const canAddContent = isOwner || (partyDetail?.allowMemberAddContent ?? false);
    async function handleAddToParty() {
        if (!partyId || isAdding)
            return;
        setIsAdding(true);
        try {
            await api.post(`/parties/${partyId}/works`, { workId });
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'watchlist'] });
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'work-ids'] });
        }
        catch (err) {
            const msg = err?.message ?? '';
            if (msg.includes('limit')) {
                setAddError(msg);
            }
        }
        finally {
            setIsAdding(false);
        }
    }
    async function handleSuggest() {
        if (!partyId || isAdding || suggested)
            return;
        setIsAdding(true);
        try {
            await api.post(`/parties/${partyId}/works/suggest`, { workId });
            setSuggested(true);
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'activity'] });
        }
        catch {
            // fail silently
        }
        finally {
            setIsAdding(false);
        }
    }
    async function handleRemoveFromParty() {
        if (!partyId || isRemoving)
            return;
        try {
            const ties = await api.get(`/parties/${partyId}/works/${workId}/ties`);
            const hasTies = ties.discussionCount > 0 || ties.messageCount > 0 || ties.activityFeedCount > 0;
            if (!hasTies) {
                await api.delete(`/parties/${partyId}/works/${workId}`);
                queryClient.invalidateQueries({ queryKey: ['party', partyId, 'watchlist'] });
                queryClient.invalidateQueries({ queryKey: ['party', partyId, 'work-ids'] });
                queryClient.invalidateQueries({ queryKey: ['party', partyId, 'activity'] });
            }
            else {
                setRemoveTies(ties);
                setRemoveConfirmOpen(true);
            }
        }
        catch {
            // fail silently
        }
    }
    async function handleConfirmRemove() {
        if (!partyId)
            return;
        setIsRemoving(true);
        try {
            await api.delete(`/parties/${partyId}/works/${workId}`);
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'watchlist'] });
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'work-ids'] });
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'activity'] });
            setRemoveConfirmOpen(false);
            setRemoveTies(null);
        }
        catch {
            // fail silently
        }
        finally {
            setIsRemoving(false);
        }
    }
    if (isLoading || !work) {
        return (_jsxs("div", { style: { display: 'flex', justifyContent: 'center', padding: '64px 0' }, children: [_jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg) } }` }), _jsx("div", { style: {
                        width: '24px', height: '24px',
                        border: '2px solid var(--color-border)',
                        borderTopColor: 'var(--color-text)',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite',
                    } })] }));
    }
    const tabs = [
        { id: 'details', label: 'Details' },
        ...(partyId && isInParty ? [{ id: 'discussion', label: 'Discussion' }] : []),
    ];
    return (_jsxs("div", { className: "work-detail-layout", children: [_jsx("div", { className: "work-detail-poster", children: _jsx("div", { style: {
                        aspectRatio: '2/3',
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden',
                        background: 'var(--color-surface-sunken)',
                    }, children: work.posterUrl
                        ? _jsx("img", { src: tmdbImage(work.posterUrl, 'w500'), alt: work.title, style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } })
                        : _jsx("div", { style: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Film, { size: 48, style: { color: 'var(--color-text-faint)' } }) }) }) }), _jsxs("div", { className: "work-detail-info", children: [_jsx("h1", { style: {
                            fontFamily: 'var(--font-family-heading)',
                            fontSize: 'clamp(1.25rem, 3vw, 2rem)',
                            fontWeight: 700,
                            color: 'var(--color-text)',
                            margin: '0 0 6px',
                            lineHeight: 1.2,
                        }, children: work.title }), _jsx("p", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.875rem',
                            color: 'var(--color-text-muted)',
                            margin: '0 0 20px',
                        }, children: [
                            work.releaseYear,
                            workTypeLabel(work.workType),
                            work.runtimeMinutes ? `${work.runtimeMinutes}m` : null,
                        ].filter(Boolean).join(' · ') }), _jsxs("div", { style: {
                            display: 'flex', alignItems: 'center', gap: '16px',
                            padding: '14px 16px',
                            background: 'var(--color-surface)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            marginBottom: '20px',
                            flexWrap: 'wrap',
                        }, children: [_jsx(WatchStatusControl, { workId: workId, currentStatus: work.myStatus, workType: work.workType }), _jsx("div", { style: { width: '1px', height: '24px', background: 'var(--color-border)' } }), _jsx(StarRating, { workId: workId, currentRating: work.myRating })] }), partyId && !isInParty && canAddContent && (_jsxs("button", { onClick: handleAddToParty, disabled: isAdding, style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-surface)',
                            color: 'var(--color-text)',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: isAdding ? 'default' : 'pointer',
                            marginBottom: '20px',
                            transition: 'border-color 80ms ease',
                        }, children: [_jsx(Plus, { size: 16 }), isAdding ? 'Adding...' : 'Add to Watch List'] })), addError && (_jsx("p", { style: { color: 'var(--color-error, #ef4444)', fontSize: '0.8125rem', marginBottom: '12px' }, children: addError })), partyId && !isInParty && !canAddContent && (_jsxs("button", { onClick: handleSuggest, disabled: isAdding || suggested, style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            background: suggested ? 'var(--color-surface-sunken)' : 'var(--color-surface)',
                            color: suggested ? 'var(--color-text-muted)' : 'var(--color-text)',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: isAdding || suggested ? 'default' : 'pointer',
                            marginBottom: '20px',
                            transition: 'border-color 80ms ease',
                        }, children: [suggested ? _jsx(Check, { size: 16 }) : _jsx(Lightbulb, { size: 16 }), suggested ? 'Suggested' : isAdding ? 'Suggesting...' : 'Suggest'] })), partyId && isInParty && isOwner && (_jsxs("button", { onClick: handleRemoveFromParty, style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-danger, #ef4444)',
                            background: 'transparent',
                            color: 'var(--color-danger, #ef4444)',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            marginBottom: '20px',
                            transition: 'border-color 80ms ease',
                        }, children: [_jsx(Trash2, { size: 16 }), "Remove from Watch List"] })), _jsxs(Dialog, { open: removeConfirmOpen, onOpenChange: (open) => { if (!open) {
                            setRemoveConfirmOpen(false);
                            setRemoveTies(null);
                        } }, children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { children: ["Remove \"", work?.title, "\"?"] }), _jsx(DialogDescription, { children: "This will permanently delete:" })] }), removeTies && (_jsxs("ul", { style: { margin: '8px 0 0', paddingLeft: '20px', fontSize: '0.875rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-body)', lineHeight: 1.8 }, children: [removeTies.discussionCount > 0 && _jsxs("li", { children: [removeTies.discussionCount, " discussion", removeTies.discussionCount !== 1 ? 's' : ''] }), removeTies.messageCount > 0 && _jsxs("li", { children: [removeTies.messageCount, " message", removeTies.messageCount !== 1 ? 's' : ''] }), removeTies.activityFeedCount > 0 && _jsxs("li", { children: [removeTies.activityFeedCount, " activity feed entr", removeTies.activityFeedCount !== 1 ? 'ies' : 'y'] })] })), _jsxs(DialogFooter, { children: [_jsx("button", { onClick: () => { setRemoveConfirmOpen(false); setRemoveTies(null); }, style: {
                                            padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)',
                                            background: 'transparent', color: 'var(--color-text)', fontSize: '0.875rem', fontWeight: 600,
                                            fontFamily: 'var(--font-family-body)', cursor: 'pointer',
                                        }, children: "Cancel" }), _jsx("button", { onClick: handleConfirmRemove, disabled: isRemoving, style: {
                                            padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none',
                                            background: 'var(--color-danger, #ef4444)', color: '#fff', fontSize: '0.875rem', fontWeight: 600,
                                            fontFamily: 'var(--font-family-body)', cursor: isRemoving ? 'not-allowed' : 'pointer',
                                            opacity: isRemoving ? 0.6 : 1,
                                        }, children: isRemoving ? 'Removing...' : 'Remove' })] })] }), tabs.length > 1 && (_jsx("div", { style: { display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '24px', overflowX: 'auto' }, children: tabs.map((tab) => (_jsx("button", { onClick: () => setActiveTab(tab.id), style: {
                                padding: '10px 16px',
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.875rem',
                                fontWeight: 700,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                borderBottom: activeTab === tab.id ? '2px solid var(--color-text)' : '2px solid transparent',
                                color: activeTab === tab.id ? 'var(--color-text)' : 'var(--color-text-muted)',
                                marginBottom: '-1px',
                                transition: 'color 80ms ease',
                                whiteSpace: 'nowrap',
                            }, children: tab.label }, tab.id))) })), activeTab === 'details' && (_jsxs(_Fragment, { children: [work.canonicalGenres?.length > 0 && (_jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }, children: work.canonicalGenres.map((genre) => (_jsx("span", { style: {
                                        padding: '3px 10px',
                                        borderRadius: 'var(--radius-full)',
                                        border: '1px solid var(--color-border)',
                                        fontFamily: 'var(--font-family-body)',
                                        fontSize: '0.75rem',
                                        color: 'var(--color-text-muted)',
                                    }, children: genre.name }, genre.canonicalGenreId))) })), work.summary && (_jsx("p", { style: {
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.9rem',
                                    lineHeight: 1.65,
                                    color: 'var(--color-text-muted)',
                                    marginBottom: '24px',
                                }, children: work.summary })), work.streamingAvailability?.length > 0 && (_jsxs("div", { style: { marginBottom: '24px' }, children: [_jsx("h3", { style: {
                                            fontFamily: 'var(--font-family-body)',
                                            fontSize: '0.75rem', fontWeight: 700,
                                            letterSpacing: '0.08em', textTransform: 'uppercase',
                                            color: 'var(--color-text-muted)',
                                            marginBottom: '12px',
                                        }, children: "Where to Watch" }), _jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '8px' }, children: work.streamingAvailability.map((s, i) => (_jsxs("span", { style: {
                                                padding: '4px 10px',
                                                borderRadius: 'var(--radius-full)',
                                                background: 'var(--color-surface)',
                                                border: '1px solid var(--color-border)',
                                                fontFamily: 'var(--font-family-body)',
                                                fontSize: '0.8rem',
                                                color: 'var(--color-text)',
                                            }, children: [s.providerName, s.accessType && (_jsxs("span", { style: { color: 'var(--color-text-muted)', fontSize: '0.7rem', marginLeft: '4px' }, children: ["(", s.accessType, ")"] }))] }, i))) })] })), work.cast?.length > 0 && (_jsxs("div", { style: { marginBottom: '24px' }, children: [_jsx("h3", { style: {
                                            fontFamily: 'var(--font-family-body)',
                                            fontSize: '0.75rem', fontWeight: 700,
                                            letterSpacing: '0.08em', textTransform: 'uppercase',
                                            color: 'var(--color-text-muted)',
                                            marginBottom: '8px',
                                        }, children: "Cast" }), _jsx("div", { style: {
                                            display: 'flex', gap: '12px',
                                            overflowX: 'auto', paddingBottom: '8px',
                                        }, children: work.cast.slice(0, 10).map((person) => (_jsx(PersonCard, { name: person.name, subtitle: person.characterName, profilePath: person.profilePath }, person.personId))) })] })), work.crew?.length > 0 && (_jsxs("div", { style: { marginBottom: '24px' }, children: [_jsx("h3", { style: {
                                            fontFamily: 'var(--font-family-body)',
                                            fontSize: '0.75rem', fontWeight: 700,
                                            letterSpacing: '0.08em', textTransform: 'uppercase',
                                            color: 'var(--color-text-muted)',
                                            marginBottom: '8px',
                                        }, children: "Crew" }), _jsx("div", { style: {
                                            display: 'flex', gap: '12px',
                                            overflowX: 'auto', paddingBottom: '8px',
                                        }, children: work.crew.slice(0, 10).map((person) => (_jsx(PersonCard, { name: person.name, subtitle: person.job, profilePath: person.profilePath }, `${person.personId}-${person.job}`))) })] })), work.workType === 2 && _jsx(SeasonsSection, { workId: workId })] })), activeTab === 'discussion' && partyId && (_jsx(DiscussionTab, { partyId: partyId, workId: workId }))] })] }));
}
